import { IButtonData } from 'cd-eft/components/common/MessageCenter/Message';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { action } from 'mobx';
import stores from '../../../models/Stores';
import { HydroTcVelocityController } from 'cd-eft/services/eftEfh2Api';
import { formatRound } from 'cd-eft/utils/mathUtilities';

const { efh2Store } = stores.AppStore;

export class GISFooterVM {
  props;
  dialogVM;
  history;
  constructor(props, dialogVM, history) {
    this.props = props;
    this.dialogVM = dialogVM;
    this.history = history;
  }

  private resolveProceedWithValues?: (value: boolean) => void;

  proceedWithValues(): Promise<boolean> {
    const currentPromise: Promise<boolean> = new Promise((resolve, reject) => {
      this.resolveProceedWithValues = resolve;
    });
    return currentPromise;
  }

  @action.bound async calculateVelocityTable() {
    const velocityVm = this.props.vm.activityViewModel?.velocityVm;
    // const isMinimumValidation = await velocityVm?.minimumValidation();
    if (!velocityVm) return;
    // Validate form before proceeding
    this.props.vm.activityViewModel.jobExecuting = true;
    if (typeof velocityVm.validateForm === 'function') {
      const isValid = await velocityVm.validateForm();

      if (!isValid) {
        this.props.vm.activityViewModel.jobExecuting = false;
        return;
      }
    }

    // Build flowPathSegments from tableData
    const flowPathSegments = velocityVm.tableData
      .filter(row => row.flowType)
      .map(row => {
        const isChannel = row.flowType === 3;
        return {
          flowTypeId: row.flowType,
          separatorX: 0.0,
          separatorY: 0.0,
          segmentLength: Number(row.length) || 0,
          segmentSlope: Number(row.slope) || 0,
          surfaceManning: !isChannel ? Number(row.surfaceMannings) || 0 : 0,
          manningn: isChannel ? Number(row.n) || 0 : 0,
          crossSectionalFlowArea: isChannel ? Number(row.area) || 0 : 0,
          wettedPerimeter: isChannel ? Number(row.perimeter) || 0 : 0,
          velocity: 0.0,
          subTimeOfConcentration: 0,
          notes: ""
        };
      });

    const rainfallDistribution = Number(velocityVm.inputHeaderRainfall) || 0;
    const sessionUuid = efh2Store.projectsStore.currentSessionId;

    const payload = {
      flowPathSegments,
      rainfallDistribution,
      sessionUuid
    };

    try {
      const response: any = await HydroTcVelocityController.calculateVelocityAndTimeOfConcentrationUsingPOST(payload);
      const result = response.data;

      // Only show error/warning if at least one property is non-null
      if (
        result &&
        result.errorResponse &&
        (
          result.errorResponse.errorMessage ||
          result.errorResponse.errorCode ||
          result.errorResponse.errorType ||
          result.errorResponse.urlPath
        )
      ) {
        if (result.errorResponse.errorType === 'Warning') {
          MessageCenter.addWarning(
            result.errorResponse.errorMessage,
            [],
            result.errorResponse.errorCode
          );
          // continue to process results!
        } else {
          MessageCenter.addError(
            result.errorResponse.errorMessage,
            [],
            result.errorResponse.errorCode
          );
          return; // Stop further processing if error
        }
      }
      efh2Store.velocityInputUpdated = false;
      efh2Store.tocManuallyUpdated = false
      if (result?.flowPathSegments) {
        result.flowPathSegments.forEach((seg, idx) => {
          if (velocityVm.tableData[idx]) {
            velocityVm.tableData[idx].velocity = seg.velocity !== undefined && seg.velocity !== null
              ? formatRound(seg.velocity, 2)
              : '';
            velocityVm.tableData[idx].time = seg.subTimeOfConcentration !== undefined && seg.subTimeOfConcentration !== null
              ? formatRound(seg.subTimeOfConcentration, 2)
              : '';
            // Clear validation for velocity and time columns
            if (velocityVm.tableValidationData[idx]) {
              velocityVm.tableValidationData[idx].velocity = false;
              velocityVm.tableValidationData[idx].time = false;
            }
          }
        });
      }
      if (result?.totalLength !== undefined) {
        velocityVm.totalLengthResult = formatRound(result.totalLength, 1);
      }
      if (result?.averageVelocity !== undefined) {
        velocityVm.averageVelocity = formatRound(result.averageVelocity, 2);
      }
      if (result?.timeOfConcentration !== undefined) {
        const roundedTime = formatRound(result.timeOfConcentration, 2);
        velocityVm.totalTime = roundedTime;
        await velocityVm.validateTime('totals-time', roundedTime, roundedTime, 0);
      }
    } catch (err) {
      console.error(err);
    }
    finally {
      this.props.vm.activityViewModel.jobExecuting = false;
    }
  }
  @action.bound async resetAction() {
    this.props.vm.activityViewModel.velocityVm.resetAction();
  }
  @action.bound accept(): void {
    if (this.resolveProceedWithValues) {
      //resolve the promise
      this.resolveProceedWithValues(true);
      //discard callback handle
      this.resolveProceedWithValues = undefined;
    }
  }

  @action.bound cancel(): void {
    if (this.resolveProceedWithValues) {
      //resolve the promise
      this.resolveProceedWithValues(false);
      //discard callback handle
      this.resolveProceedWithValues = undefined;
    }
  }

  private notificationCancelButton: IButtonData = {
    callback: () => {
      this.cancel();
      MessageCenter.clearMessages();
    },
    text: 'Close',
  };

  @action.bound async nextFromLandTable() {
    const { vm } = this.props;
    const step = vm.activityViewModel.currentStepIndex;
    try {
      vm.clearNotification();
      if (vm && vm.activityViewModel) {
        // ensure user accepts changes, if back button was used
        if (
          (vm.activityViewModel.hasRedo && !vm.activityViewModel.canRedo) ||
          ( /* vm.activityViewModel.hasRedo && */ step > 2 && vm.activityViewModel.result_soils_layer && !vm.activityViewModel.canRedoLandUse)
        ) {
          const title = 'Changes were made';
          const message =
            'Proceeding with these changes will replace the previous results from future steps. Are you sure you want to continue?';
          const elementToFocus = document.activeElement;
          const dialogResult: boolean = await this.dialogVM.showDialog(message, title);
          this.dialogVM.refocus(elementToFocus);

          if (dialogResult !== true) {
            return;
          }
          vm.activityViewModel.deleteSoilData();
          vm.activityViewModel.emptyRedoStack();
          await vm.activityViewModel.invalidateGpSession();
        }
        if (efh2Store.projectsStore.landErrors) {
          vm.activityViewModel.landUseTableComplete = false;
          throw new Error('Please make sure all missing values are entered for the Land Use table.');
        }
        if (!efh2Store.projectsStore.landErrors) {
          vm.activityViewModel.landUseTableComplete = true;
          await vm.activityViewModel.saveLandUse();
          if (
            vm.activityViewModel.edit_SAL_LANDUSE_graphicsLayerView &&
            vm.activityViewModel.edit_SAL_LANDUSE_graphicsLayerView.layer
          ) {
            vm.activityViewModel.edit_SAL_LANDUSE_graphicsLayerView.layer.visible = false;
          }
          if (vm.activityViewModel.result_soils_layer) {
            vm.activityViewModel.result_soils_layer.visible = true;
          } else {
            vm.activityViewModel.landUseTableComplete = false;
            await vm.activityViewModel.submit(this.history);
            if (vm.activityViewModel.hasSoilError) {
              if (
                vm.activityViewModel.edit_SAL_LANDUSE_graphicsLayerView &&
                vm.activityViewModel.edit_SAL_LANDUSE_graphicsLayerView.layer
              ) {
                vm.activityViewModel.edit_SAL_LANDUSE_graphicsLayerView.layer.visible = true;
              }
              if (vm.activityViewModel.result_soils_layer) {
                vm.activityViewModel.result_soils_layer.visible = false;
              }
              vm.activityViewModel.hasSoilError = false;
            } else {
              vm.activityViewModel.landUseTableComplete = true;
            }
          }
          if (vm.activityViewModel.landUseTableComplete) {
            vm.activityViewModel.salSelectGraphicsLayer.graphics.removeAll();
            vm.activityViewModel.forceSyncSoilSelection();
          }
        }
      }
    } catch (error) {
      console.error(error);
      vm.WatershedActivityNotification(error);
    }
  }

  @action.bound backFromSoilTable() {
    const { vm } = this.props;
    if (vm && vm.activityViewModel) {
      vm.clearNotification();
      vm.activityViewModel.hydrologyValidationError = false;
      vm.activityViewModel.createSnapShot();
      vm.activityViewModel.landUseTableComplete = false;
      if (vm.activityViewModel.result_soils_layer) {
        vm.activityViewModel.result_soils_layer.visible = false;
      }
      if (vm.activityViewModel.salSoilGraphicsLayer) {
        vm.activityViewModel.salSoilGraphicsLayer.visible = false;
      }
      if (
        vm.activityViewModel.edit_SAL_LANDUSE_graphicsLayerView &&
        vm.activityViewModel.edit_SAL_LANDUSE_graphicsLayerView.layer
      ) {
        vm.activityViewModel.edit_SAL_LANDUSE_graphicsLayerView.layer.visible = true;
      }
      vm.activityViewModel.salSelectGraphicsLayer.graphics.removeAll();
      vm.activityViewModel.syncLandUseSelection();
    }
  }

  @action.bound async onSubmit() {
    const { vm } = this.props;
    const step = vm.activityViewModel.currentStepIndex;
    try {
      if (vm && vm.activityViewModel) {
        //clear existing notifications
        vm.clearNotification();
        if (step === 0) {
          vm.activityViewModel.deactivateSketchTools(vm.activityViewModel.aoiSketch);
        }
        if (step === 1) {
          vm.activityViewModel.deactivateSketchTools(vm.activityViewModel.outletSketch);
          vm.activityViewModel.deactivateSketchTools(vm.activityViewModel.culvertSketch);
        }
        if (step === 2) {
          vm.activityViewModel.deactivateSketchTools(vm.activityViewModel.watershedSketch);
          vm.activityViewModel.deactivateSketchTools(vm.activityViewModel.flowPathSketch);
          vm.activityViewModel.canResetSmartCut = false;
        }
        if (step === 2 && this.props.vm.activityViewModel.velocityVm) {
          await this.props.vm.activityViewModel.velocityVm.callBothApis();
        }
        if (vm.activityViewModel.canRedo && !efh2Store.tocManuallyUpdated) {
          // can skip validation for redo operation
          vm.activityViewModel.redo();
        } else {
          // run validators before submit

          if (step === 0) {
            if (!vm.activityViewModel.hasAoiGraphics) {
              throw new Error('Invalid operation: Please define an AOI.');
            }
            if (!efh2Store.hasValidAoiPolygon) {
              throw new Error('Invalid operation: Please define a valid AOI.');
            }
            if (vm.activityViewModel.aoiGraphicsCount !== 1) {
              throw new Error('Invalid operation: You may only supply one AOI boundary.');
            }
            if (!vm.activityViewModel.hasValidAoiGraphics) {
              throw new Error('Invalid operation: AOI must be completely within the DEM boundary.');
            }
            if (vm.activityViewModel.in_dem_service_selection === 'service') {
              const demClipped: boolean = await vm.activityViewModel.demClip();
              if (!demClipped) {
                return;
              }
            }
          }

          if (step === 1 || (step === 0 && vm.activityViewModel.hasOutletGraphics)) {
            if (!vm.activityViewModel.hasOutletGraphics) {
              throw new Error('Invalid operation: Please define an outlet.');
            }
            if (vm.activityViewModel.outletGraphicsCount !== 1) {
              throw new Error('Invalid operation: You may only supply one Outlet.');
            }
            if (!vm.activityViewModel.hasValidOutletGraphics) {
              throw new Error('Invalid operation: Outlet must be completely within the AOI boundary.');
            }
          }

          if (step < 2 && efh2Store.aoiContainsWatershed === false) {
            efh2Store.aoiContainsWatershed = true;
          }
          if (step < 2 && efh2Store.hasValidWatershedPolygon === false) {
            efh2Store.hasValidWatershedPolygon = true;
          }
          if (step === 2 && !vm.activityViewModel.hasValidWatershedGraphics) {
            efh2Store.aoiContainsWatershed = false;
            throw new Error('Invalid operation: Watershed must be completely within the AOI boundary.');
          }

          if (step === 3 && efh2Store.projectsStore.landErrors) {
            vm.activityViewModel!.landUseTableComplete = false;
            throw new Error('Please make sure all missing values are entered for the Land Use table.');
          }

          if (step === 3 && efh2Store.projectsStore.soilErrors) {
            throw new Error('Please make sure all missing values are entered for the Soil table.');
          }

          // ensure user accepts changes, if back button was used
          if (
            (vm.activityViewModel.hasRedo && !vm.activityViewModel.canRedo) || (step === 2 && efh2Store.tocManuallyUpdated) ||
            (vm.activityViewModel.hasRedo && step > 2 && !vm.activityViewModel.canRedoLandUse)
          ) {
            const title = 'Changes were made';
            const message =
              'Proceeding with these changes will replace the previous results from future steps. Are you sure you want to continue?';
            const elementToFocus = document.activeElement;
            const dialogResult: boolean = await this.dialogVM.showDialog(message, title);
            this.dialogVM.refocus(elementToFocus);
            if (dialogResult !== true) {
              return;
            }
            // CONTINUE: user accepts, do not short-circuit

            if (step <= 2) {
              vm.activityViewModel.deleteSavedLandUse();
              vm.activityViewModel.deleteSoilData();
            }

            if (step === 0) {
              if (
                vm.activityViewModel.in_dem_service_selection === 'service' /* && vm.activityViewModel.aoiModified */
              ) {
                const demClipped: boolean = await vm.activityViewModel.demClip();
                if (!demClipped) {
                  return;
                }
              }
            }
          }

          // Checks the max flow length and avg slope when clicking Next from the Edit Watershed step
          // Also checking watershed area
          if (step === 2) {
            const result_watershed = vm.activityViewModel.result_watershed;
            if (!(result_watershed &&
              result_watershed.features &&
              (result_watershed.features.length > 0) &&
              result_watershed.features[0].attributes)) {
              vm.activityViewModel.hydrologyValidationError = true;
              return;
            }
            const flowLength = result_watershed.features[0].attributes.MaxFlowLengthFt;
            const slope = result_watershed.features[0].attributes.Avg_Slope;
            const area = result_watershed.features[0].attributes.Acres;

            if ((!flowLength) || (!(200 <= flowLength && flowLength <= 26000))) {
              vm.activityViewModel.hydrologyValidationError = true;
              await MessageCenter.addErrorFromCode('Hydro_14', [this.notificationCancelButton]);
              const dialogResult: boolean = await this.proceedWithValues();
              if (!dialogResult) {
                return;
              }
            }
            if ((!slope) || (!(0.5 <= slope && slope <= 64))) {
              vm.activityViewModel.hydrologyValidationError = true;
              await MessageCenter.addErrorFromCode('Hydro_49', [this.notificationCancelButton]);
              const dialogResult: boolean = await this.proceedWithValues();
              if (!dialogResult) {
                return;
              }
            }
            if ((!area) || (area < 0.096)) {
              vm.activityViewModel.hydrologyValidationError = true;
              await MessageCenter.addErrorFromCode('Hydro_68', [this.notificationCancelButton]);
              const dialogResult: boolean = await this.proceedWithValues();
              if (!dialogResult) {
                return;
              }
            }
            if (area > 2000) {
              await MessageCenter.addWarningFromCode('Hydro_13', [
                {
                  callback: () => {
                    MessageCenter.clearMessages();
                    this.cancel();
                  },
                  text: 'OK',
                },
              ]);
            }
          }

          // save user preferences every time user clicks next
          vm.loadingResults = true;
          vm.updateMapPreferences();

          await vm.activityViewModel.submit(this.history);
          await vm.getAndAcceptResults();
        }
      }
    } catch (error) {
      console.error(error);
      vm.WatershedActivityNotification(error);
    } finally {
      // nothing to do here
      vm.loadingResults = false;
    }
  }
}
